import React from 'react';
import { ShieldCheckIcon, EyeIcon, MonitorIcon, KeyboardIcon, PhoneIcon, MailIcon } from 'lucide-react';
export function Accessibility() {
  return <section className="py-20 bg-white" data-id="element-35">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-36">
        <div className="mb-12" data-id="element-37">
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-bold mb-6" data-id="element-38">
            <ShieldCheckIcon className="w-4 h-4" data-id="element-39" />
            WCAG 2.1 AA Compliant
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-secondary mb-4" data-id="element-40">
            Accessibility Statement
          </h1>
          <p className="text-gray-600 text-lg" data-id="element-41">Last updated: March 2026</p>
        </div>

        <div className="space-y-12" data-id="element-42">
          {/* Commitment */}
          <div data-id="element-43">
            <h2 className="text-2xl font-bold text-secondary mb-4" data-id="element-44">
              Our Commitment
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed" data-id="element-45">
              Uzoka Tow Truck Service is committed to ensuring digital
              accessibility for people with disabilities. We are continually
              improving the user experience for everyone and applying the
              relevant accessibility standards to ensure we provide equal access
              to all users.
            </p>
          </div>

          {/* Standards */}
          <div data-id="element-46">
            <h2 className="text-2xl font-bold text-secondary mb-4" data-id="element-47">
              Conformance Status
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-4" data-id="element-48">
              We aim to conform to the Web Content Accessibility Guidelines
              (WCAG) 2.1 at Level AA. These guidelines explain how to make web
              content more accessible to people with a wide range of
              disabilities.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8" data-id="element-49">
              <div className="bg-accent rounded-[10px] p-6" data-id="element-50">
                <EyeIcon className="w-8 h-8 text-primary mb-3" data-id="element-51" />
                <h3 className="font-bold text-secondary mb-2" data-id="element-52">Perceivable</h3>
                <p className="text-gray-600 text-sm" data-id="element-53">
                  All images include descriptive alt text. Color contrast meets
                  AA standards. Text is resizable up to 200%.
                </p>
              </div>
              <div className="bg-accent rounded-[10px] p-6" data-id="element-54">
                <KeyboardIcon className="w-8 h-8 text-primary mb-3" data-id="element-55" />
                <h3 className="font-bold text-secondary mb-2" data-id="element-56">Operable</h3>
                <p className="text-gray-600 text-sm" data-id="element-57">
                  Full keyboard navigation support. Focus indicators on all
                  interactive elements. No time-limited interactions.
                </p>
              </div>
              <div className="bg-accent rounded-[10px] p-6" data-id="element-58">
                <MonitorIcon className="w-8 h-8 text-primary mb-3" data-id="element-59" />
                <h3 className="font-bold text-secondary mb-2" data-id="element-60">
                  Understandable
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-61">
                  Clear, consistent navigation. Plain language throughout. Error
                  messages provide helpful guidance.
                </p>
              </div>
            </div>
          </div>

          {/* Measures */}
          <div data-id="element-62">
            <h2 className="text-2xl font-bold text-secondary mb-4" data-id="element-63">
              Measures We Take
            </h2>
            <ul className="space-y-3" data-id="element-64">
              {['Include accessibility as part of our development process and internal policies.', 'Conduct regular accessibility audits using both automated tools and manual testing.', 'Provide ongoing accessibility training for our development and content teams.', 'Use semantic HTML elements and ARIA attributes where appropriate.', 'Ensure all forms are properly labeled and provide clear error messaging.', 'Maintain sufficient color contrast ratios across all pages.', 'Support screen readers and other assistive technologies.', 'Ensure responsive design works across all devices and screen sizes.'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-65">
                  <ShieldCheckIcon className="w-5 h-5 text-green-500 shrink-0 mt-0.5" data-id="element-66" />
                  <span className="text-gray-600 text-lg" data-id="element-67">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* Known Limitations */}
          <div data-id="element-68">
            <h2 className="text-2xl font-bold text-secondary mb-4" data-id="element-69">
              Known Limitations
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed" data-id="element-70">
              While we strive for full accessibility, some third-party content
              or older pages may not yet fully meet all WCAG 2.1 AA criteria. We
              are actively working to identify and resolve these issues. If you
              encounter any barriers, please contact us so we can assist you.
            </p>
          </div>

          {/* Feedback */}
          <div className="bg-secondary rounded-[10px] p-8 text-white" data-id="element-71">
            <h2 className="text-2xl font-bold mb-4" data-id="element-72">Feedback & Contact</h2>
            <p className="text-white/80 text-lg leading-relaxed mb-6" data-id="element-73">
              We welcome your feedback on the accessibility of our website. If
              you encounter any accessibility barriers or have suggestions for
              improvement, please reach out to us:
            </p>
            <div className="space-y-4" data-id="element-74">
              <div className="flex items-center gap-3" data-id="element-75">
                <PhoneIcon className="w-5 h-5 text-primary" data-id="element-76" />
                <span className="text-lg" data-id="element-77">718.210.3110</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-78">
                <MailIcon className="w-5 h-5 text-primary" data-id="element-79" />
                <span className="text-lg" data-id="element-80">accessibility@uzomatowing.com</span>
              </div>
            </div>
            <p className="text-white/60 mt-6 text-sm" data-id="element-81">
              We aim to respond to accessibility feedback within 2 business
              days.
            </p>
          </div>
        </div>
      </div>
    </section>;
}